import { Settings2, Eye, EyeOff, GripVertical } from 'lucide-react';
import type { ColumnPreference } from '../../../lib/api';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

interface ColumnManagerProps {
  columns: ColumnPreference[];
  onToggle: (columnId: string) => void;
  onSort: (columns: { column_id: string; sort_order: number }[]) => void;
}

export default function ColumnManager({ columns, onToggle, onSort }: ColumnManagerProps) {
  const sorted = [...columns].sort((a, b) => a.sort_order - b.sort_order);

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const updated = sorted.map((col, i) => {
      if (i === index) return { column_id: col.column_id, sort_order: i - 1 };
      if (i === index - 1) return { column_id: col.column_id, sort_order: i + 1 };
      return { column_id: col.column_id, sort_order: i };
    });
    onSort(updated);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings2 className="h-4 w-4" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-h-96 overflow-y-auto p-2">
        <div className="space-y-1">
          {sorted.map((col, index) => (
            <div
              key={col.column_id}
              className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted"
            >
              <button
                className="flex items-center gap-2 flex-1 text-left text-sm"
                onClick={() => onToggle(col.column_id)}
              >
                {col.is_visible ? (
                  <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-50" />
                )}
                <span className={col.is_visible ? '' : 'text-muted-foreground opacity-50'}>
                  {col.label}
                </span>
              </button>
              <div className="flex gap-0.5">
                <button
                  className="p-0.5 hover:bg-background rounded"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                >
                  <GripVertical className="h-3 w-3 rotate-90 text-muted-foreground" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
